import { useEffect, useState } from 'react';
import { authApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './settings/Section';
import { Button } from '@verlo/shared/components/ui/button';
import { toast } from 'sonner';
import { LogOut, Loader2 } from 'lucide-react';

// Auth item 7 — the "these are my logged-in devices" screen, with a per-device
// sign-out and a "sign out everywhere else". Revocation takes effect on that
// device's next request (the access token is only good for 15 minutes and the
// session is checked on every call).

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 90) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// A very light UA read — enough to tell one row from another, no library.
const describe = (ua) => {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  // iOS first: an iPhone says it is "like Mac OS X".
  const os = /Windows/.test(ua) ? 'Windows'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Macintosh|Mac OS X/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android'
    : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
};

const MyDevicesCard = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () => authApi.listSessions()
    .then(({ data }) => setRows(data))
    .catch(() => setRows([]));

  useEffect(() => { load(); }, []);

  const endOne = async (id) => {
    setBusy(id);
    try {
      await authApi.endSession(id);
      toast.success('Signed that device out');
      load();
    } catch {
      toast.error('Could not sign that device out');
    } finally {
      setBusy('');
    }
  };

  const endOthers = async () => {
    setBusy('others');
    try {
      const { data } = await authApi.revokeOtherSessions();
      toast.success(data.ended ? `Signed out ${data.ended} other device${data.ended > 1 ? 's' : ''}` : 'No other devices');
      load();
    } catch {
      toast.error('Could not sign out other devices');
    } finally {
      setBusy('');
    }
  };

  const others = (rows || []).filter((r) => !r.current).length;

  return (
    <Card data-testid="my-devices-card">
      <CardHeader>
        <CardTitle>Signed-in devices</CardTitle>
        <CardDescription>
          Every place your account is currently logged into the back office.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows === null ? (
          <div className="flex items-center gap-2 text-sm text-ink-faint py-3">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-ink-faint py-3">No active sessions.</p>
        ) : (
          <div className="divide-y rounded-lg border">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5"
                   data-testid={`device-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {describe(r.user_agent)}
                    {r.current && <span className="ml-2 text-[11px] text-signal-ok bg-signal-ok-tint px-1.5 py-0.5 rounded">This device</span>}
                    {r.remember && <span className="ml-2 text-[11px] text-ink-faint">remembered</span>}
                  </p>
                  <p className="text-[12px] text-ink-faint">
                    {r.ip || 'unknown IP'} · active {fmtWhen(r.last_seen_at)}
                  </p>
                </div>
                {!r.current && (
                  <Button variant="ghost" size="sm" className="text-ink-soft hover:text-signal-stop shrink-0"
                          onClick={() => endOne(r.id)} disabled={busy === r.id}
                          data-testid={`device-end-${r.id}`}>
                    {busy === r.id ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {others > 0 && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={endOthers} disabled={busy === 'others'}
                    data-testid="device-end-others">
              {busy === 'others' && <Loader2 size={14} className="animate-spin mr-1.5" />}
              Sign out everywhere else
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyDevicesCard;
